import type { UserOrganization } from '@outpost/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AtSign, X } from 'lucide-react';
import { useState } from 'react';

import { ErrorState, ListSkeleton } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth';
import { parseDomains } from '@/lib/domains';

/**
 * The email domains an organization trusts.
 *
 * Shown to everyone, because an admin whose invitation was refused, or whose
 * alert rule would not save, needs to see why. Changed by owners only, and never
 * by a connected assistant: extending the list is exactly what someone trying
 * to send the organization's errors elsewhere would do first.
 */
export function AllowedDomains({ organization }: { organization: UserOrganization }) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const isOwner = organization.role === 'owner';
  const [draft, setDraft] = useState('');

  const queryKey = ['allowed-domains', organization.id];
  const domains = useQuery({
    queryKey,
    queryFn: () => client.getAllowedDomains(organization.id),
  });

  const save = useMutation({
    mutationFn: (next: string[]) => client.setAllowedDomains(organization.id, next),
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKey, saved);
      setDraft('');
    },
  });

  const current = domains.data ?? [];
  const additions = parseDomains(draft).filter((domain) => !current.includes(domain));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <AtSign className="size-4" />
          Allowed email domains
        </CardTitle>
        <CardDescription>
          {current.length === 0
            ? 'None listed: anyone can be invited, and alert emails can go only to members of this organization.'
            : 'Invitations are limited to addresses at these domains, and alert emails can go to members or to addresses at them.'}{' '}
          A domain does not include its subdomains. Invitations already sent and alert rules already
          saved keep working when this changes.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-3">
        {domains.isLoading && <ListSkeleton rows={1} />}
        {domains.isError && (
          <ErrorState error={domains.error} onRetry={() => void domains.refetch()} />
        )}

        {current.length > 0 && (
          <ul className="flex flex-wrap gap-2" aria-label="Allowed domains">
            {current.map((domain) => (
              <li key={domain}>
                <Badge variant="outline" className="gap-1 py-1 font-mono">
                  {domain}
                  {isOwner && (
                    <button
                      type="button"
                      className="rounded-sm text-muted-foreground hover:text-destructive"
                      aria-label={`Remove ${domain}`}
                      title={`Remove ${domain}`}
                      disabled={save.isPending}
                      onClick={() => save.mutate(current.filter((listed) => listed !== domain))}
                    >
                      <X className="size-3" />
                    </button>
                  )}
                </Badge>
              </li>
            ))}
          </ul>
        )}

        {isOwner ? (
          <form
            className="flex max-w-lg gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (additions.length > 0) save.mutate([...current, ...additions]);
            }}
          >
            <Input
              aria-label="Domains to allow"
              placeholder="example.com, example.org"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              disabled={save.isPending}
            />
            <Button
              type="submit"
              variant="outline"
              disabled={additions.length === 0 || save.isPending}
            >
              Add
            </Button>
          </form>
        ) : (
          <p className="text-xs text-muted-foreground">Only owners can change this list.</p>
        )}

        {save.error && <p className="text-sm text-destructive">{save.error.message}</p>}
      </CardContent>
    </Card>
  );
}
